import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { userRequest } from '../api/requestMethods';
import { stashCardAuthContext } from "../utils/cardPayment";
import { withFreightCheckoutConsent } from "./freightCheckout";
import { isScheduleRejectionCode } from "../utils/deliverySchedule";

const loadOrderFromLocalStorage = () => {
    const savedOrder = localStorage.getItem('retailerWholesalerOrder');
    return savedOrder ? JSON.parse(savedOrder) : '';
};

const saveOrderToLocalStorage = (order) => {
    localStorage.setItem('retailerWholesalerOrder', JSON.stringify(order));
};

const initialState = {
    order: loadOrderFromLocalStorage(),
    orderCreationStatus: '',
    paymentCreationStatus: 'idle',
    error: null,
};

// MTN Mobile Money
export const payMoMoCheckout = createAsyncThunk(
    'retailerWholesalerOrder/payMoMoCheckout',
    async (data, { rejectWithValue }) => {
        const { client_id } = data;
        try {
            const response = await userRequest.post(`/momo/${client_id}`, data);
            return response.data;
        } catch (error) {
            console.log(error.response?.data?.msg || error.message);
            return rejectWithValue(error.response?.data?.msg || error.message);
        }
    }
);

// Airtel Money
export const payAirtelMoneyCheckout = createAsyncThunk(
    'retailerWholesalerOrder/payAirtelCheckout',
    async (data, { rejectWithValue }) => {
        const { client_id } = data;
        try {
            const response = await userRequest.post(
                `/fw/flutterAirtelPayment/${client_id}`,
                data
            );
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.msg || error.message, {
                autoClose: false,
            });
            return rejectWithValue(error.response?.data?.msg || error.message);
        }
    }
);

// Payment with Credit Card
export const payCardCheckout = createAsyncThunk(
    'retailerWholesalerOrder/payCardCheckout',
    async (data, { rejectWithValue }) => {
        const { client_id } = data;
        // Stash sanitized context (last4/brand/amount) for the /pay/authorize page —
        // screens navigate there via window.location, so redux state does not survive.
        stashCardAuthContext(data?.cardDetails, data?.totalPrice);
        try {
            const response = await userRequest.post(
                `/fw/flutterCardPayment/${client_id}`,
                data,
                { withCredentials: true }
            );
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message, {
                autoClose: false,
            });
            return rejectWithValue(error.message || 'Unknown Error Occured!');
        }
    }
);

// Card payment PIN verification
export const submitCardPin_Otp = createAsyncThunk(
    'retailerWholesalerOrder/submitCardPin_Otp',
    async (data, { rejectWithValue }) => {
        const { client_id } = data;
        try {
            const response = await userRequest.post(
                `/fw/flutterSubmitCardPin_Otp/${client_id}`,
                data,
                { withCredentials: true }
            );
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message, {
                autoClose: false,
            });
            return rejectWithValue(error.message || 'Unknown Error Occured!');
        }
    }
);

export const createOrder = createAsyncThunk(
    'retailerWholesalerOrder/createOrder',
    async (data, { rejectWithValue, getState }) => {
        try {
            const checkoutData = withFreightCheckoutConsent(data, getState(), "WEB");
            const res = await userRequest('/mobile', {
                method: 'POST',
                data: checkoutData,
            });
            toast.success('Order created successfully!');
            return res.data;
        } catch (err) {
            console.log(err, "Error>>>>");
            // Optional chaining: the RW thunk previously crashed on network
            // errors (err.response undefined). Surface the backend message —
            // for HTTP 422 (OUT_OF_DELIVERY_RANGE / VENDOR_COORDS_MISSING) this
            // explains the delivery-range problem instead of a generic failure.
            const status = err.response?.status;
            const message =
                err.response?.data?.message ||
                err.message ||
                'Failed to create order. Please try again.';
            // A delivery-time refusal opens the checkout's time picker dialog
            // instead (useCheckoutDeliverySchedule); a toast would double it.
            if (!isScheduleRejectionCode(err.response?.data?.code)) {
                toast.error(message, {
                    autoClose: false,
                });
            }
            // Reject with a serializable payload so the place-order screen can
            // detect the failure (and 422 specifically) and stop the flow
            // before initiating payment / navigating to success.
            return rejectWithValue({
                status,
                code: err.response?.data?.code,
                message,
            });
        }
    }
);

const retailerWholesalerOrderSlice = createSlice({
    name: 'retailerWholesalerOrder',
    initialState,
    reducers: {
        resetOrderCreation: (state) => {
            state.order = '';
            state.orderCreationStatus = '';
            state.paymentCreationStatus = '';
            state.error = null;
            localStorage.removeItem('retailerWholesalerOrder');
        },
        resetRetailerWholesalerOrder: (state) => {
            return initialState;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(createOrder.pending, (state) => {
                state.orderCreationStatus = 'pending';
            })
            .addCase(createOrder.fulfilled, (state, action) => {
                state.orderCreationStatus = 'fulfilled';
                state.order = action.payload;
                saveOrderToLocalStorage(action.payload);
            })
            .addCase(createOrder.rejected, (state, action) => {
                state.orderCreationStatus = 'rejected';
                state.error = action.payload;
            })
            .addCase(payMoMoCheckout.pending, (state) => {
                state.paymentCreationStatus = 'pending';
            })
            .addCase(payMoMoCheckout.fulfilled, (state) => {
                state.paymentCreationStatus = 'fulfilled';
            })
            .addCase(payMoMoCheckout.rejected, (state, action) => {
                state.paymentCreationStatus = 'rejected';
                state.error = action.payload;
            })
            .addCase(payCardCheckout.pending, (state) => {
                state.paymentCreationStatus = 'pending';
            })
            .addCase(payCardCheckout.fulfilled, (state, action) => {
                state.paymentCreationStatus = 'fulfilled';
                state.order = action.payload;
                saveOrderToLocalStorage(action.payload);
            })
            .addCase(payCardCheckout.rejected, (state, action) => {
                state.paymentCreationStatus = 'rejected';
                state.error = action.payload;
            })
            .addCase(payAirtelMoneyCheckout.pending, (state) => {
                state.paymentCreationStatus = 'processing payment';
            })
            .addCase(payAirtelMoneyCheckout.fulfilled, (state, action) => {
                state.paymentCreationStatus = 'fulfilled';
                state.order = action.payload;
                saveOrderToLocalStorage(action.payload);
            })
            .addCase(payAirtelMoneyCheckout.rejected, (state, action) => {
                state.paymentCreationStatus = 'rejected';
                state.error = action.payload;
            })
            .addCase(submitCardPin_Otp.pending, (state) => {
                state.paymentCreationStatus = 'pending';
            })
            .addCase(submitCardPin_Otp.fulfilled, (state, action) => {
                state.paymentCreationStatus = 'fulfilled';
                state.order = action.payload;
                saveOrderToLocalStorage(action.payload);
            })
            .addCase(submitCardPin_Otp.rejected, (state, action) => {
                state.paymentCreationStatus = 'rejected';
                state.error = action.payload;
            });
    },
});

export const { resetOrderCreation, resetRetailerWholesalerOrder } = retailerWholesalerOrderSlice.actions;
export default retailerWholesalerOrderSlice.reducer;
